// IMPORT (HARDEN): a ClickUp CSV export (Everything › Export, "all columns") read as a whole
// workspace: its spaces become spaces, its lists boards (a folder's list is named
// "Folder / List"), its assignees people to map, its rows tasks (subtasks under their parent).
// Pure; the API reads the CSV (services/api imports/csv.ts) and resolves and writes the plan
// (imports/runs). ClickUp's API importer (`clickup.ts`) needs a token; this one needs the file.
//
// Columns, compared normalised (`normalizeHeader`): `task_name` and `list_name` are required;
// `task_id`, `task_content`, `status`, `priority`, `due_date`, `start_date`, `time_estimated`
// (milliseconds, ClickUp's export), `assignees` (`[Ana Lima, Bob]`), `space_name` (default
// "ClickUp"), `folder_name` and `parent_id` are read when present; any other column is ignored.
// Cells use csv-tasks' readers: priority names or 0–4, dates as Unix ms or ISO. A row without a
// title is skipped and counted; an unreadable cell is dropped with a warning (the task still
// imports).
import { normalizeHeader, parsePriority, parseWhen } from "./csv-tasks";

export const CLICKUP_DEFAULT_SPACE = "ClickUp";
export const CLICKUP_REQUIRED_COLUMNS = ["task_name", "list_name"] as const;
/** The board / space name limit of the work contracts. */
const NAME_MAX = 200;
const TITLE_MAX = 500;

export type ClickUpCsvSpace = { key: string; name: string; tasks: number };
export type ClickUpCsvList = {
  key: string;
  name: string;
  spaceKey: string;
  tasks: number;
};
export type ClickUpCsvPerson = { key: string; name: string; tasks: number };
export type ClickUpCsvTask = {
  /** 1-based data row. */
  row: number;
  /** ClickUp's task id, else `row:<n>` (then a re-upload of an edited file may differ). */
  externalId: string;
  listKey: string;
  title: string;
  description: string;
  status: string | null;
  priority: number | null;
  dueAt: string | null;
  startAt: string | null;
  estimateMinutes: number | null;
  assigneeKeys: string[];
  /** The parent's `externalId` when the parent is in the file. */
  parentExternalId: string | null;
};

export type ClickUpCsvPlan = {
  spaces: ClickUpCsvSpace[];
  lists: ClickUpCsvList[];
  people: ClickUpCsvPerson[];
  /** Parents before their subtasks; otherwise file order. */
  tasks: ClickUpCsvTask[];
  counts: { rows: number; tasks: number; subtasks: number; skippedNoTitle: number };
  warnings: string[];
};

const lower = (s: string) => s.trim().toLowerCase();
const cap = (s: string, max: number) => (s.length <= max ? s : s.slice(0, max).trimEnd());

export const clickUpSpaceKey = (space: string) => `clickup:space:${lower(space)}`;
export const clickUpPersonKey = (name: string) => `clickup:person:${lower(name)}`;

/** `[Ana Lima, Bob]`, `Ana Lima, Bob` or `Ana;Bob` → names. */
export function clickUpAssignees(cell: string): string[] {
  const v = cell.trim().replace(/^\[/, "").replace(/\]$/, "");
  return v
    .split(/[,;|]/)
    .map((x) => x.trim())
    .filter((x) => x !== "");
}

/** The columns `planClickUpCsv` cannot do without that `header` lacks (normalised names). */
export function missingClickUpColumns(header: readonly string[]): string[] {
  const cols = new Set(header.map(normalizeHeader));
  return CLICKUP_REQUIRED_COLUMNS.filter((c) => !cols.has(c));
}

/** `rows` are the data rows by normalised header (as the CSV readers return them). */
export function planClickUpCsv(rows: readonly Record<string, string>[]): ClickUpCsvPlan {
  const spaces = new Map<string, ClickUpCsvSpace>();
  const lists = new Map<string, ClickUpCsvList>();
  const people = new Map<string, ClickUpCsvPerson>();
  const warnings: string[] = [];
  const tasks: ClickUpCsvTask[] = [];
  const counts = { rows: rows.length, tasks: 0, subtasks: 0, skippedNoTitle: 0 };
  const seen = new Set<string>();
  const cell = (r: Record<string, string>, k: string) => (r[k] ?? "").trim();

  rows.forEach((r, i) => {
    const row = i + 1;
    const title = cap(cell(r, "task_name"), TITLE_MAX);
    if (title === "") {
      counts.skippedNoTitle++;
      return;
    }
    const spaceName = cap(cell(r, "space_name") || CLICKUP_DEFAULT_SPACE, NAME_MAX);
    const spaceKey = clickUpSpaceKey(spaceName);
    let space = spaces.get(spaceKey);
    if (!space) {
      space = { key: spaceKey, name: spaceName, tasks: 0 };
      spaces.set(spaceKey, space);
    }
    space.tasks++;
    const folder = cell(r, "folder_name");
    const listName = cell(r, "list_name") || "List";
    // ClickUp names a list outside any folder's "folder" `hidden` in some exports.
    const inFolder = folder !== "" && lower(folder) !== "hidden";
    const listKey = `clickup:list:${lower(spaceName)}/${inFolder ? lower(folder) : ""}/${lower(listName)}`;
    let list = lists.get(listKey);
    if (!list) {
      list = {
        key: listKey,
        name: cap(inFolder ? `${folder} / ${listName}` : listName, NAME_MAX),
        spaceKey,
        tasks: 0,
      };
      lists.set(listKey, list);
    }
    list.tasks++;

    let externalId = cell(r, "task_id") || `row:${row}`;
    if (seen.has(externalId)) {
      warnings.push(`row ${row}: task id ${externalId} appears twice; this row is kept apart`);
      externalId = `${externalId}#row:${row}`;
    }
    seen.add(externalId);

    const assigneeKeys: string[] = [];
    for (const name of clickUpAssignees(cell(r, "assignees"))) {
      const key = clickUpPersonKey(name);
      let p = people.get(key);
      if (!p) {
        p = { key, name, tasks: 0 };
        people.set(key, p);
      }
      p.tasks++;
      if (!assigneeKeys.includes(key)) assigneeKeys.push(key);
    }

    const read = <T>(column: string, parse: (v: string) => T | null): T | null => {
      const v = cell(r, column);
      if (v === "") return null;
      const out = parse(v);
      if (out === null) warnings.push(`row ${row}: ${column} "${v.slice(0, 40)}" is unreadable`);
      return out;
    };
    const estimateMs = read("time_estimated", (v) => (/^\d+$/.test(v) ? Number(v) : null));
    tasks.push({
      row,
      externalId,
      listKey,
      title,
      description: cell(r, "task_content"),
      status: cell(r, "status") || null,
      priority: read("priority", parsePriority),
      dueAt: read("due_date", parseWhen),
      startAt: read("start_date", parseWhen),
      estimateMinutes: estimateMs === null ? null : Math.round(estimateMs / 60_000),
      assigneeKeys,
      parentExternalId: cell(r, "parent_id") || null,
    });
  });

  // Subtasks after their parents; a parent missing from the file leaves a top-level task.
  const byId = new Map(tasks.map((t) => [t.externalId, t]));
  for (const t of tasks) {
    if (t.parentExternalId === null) continue;
    if (!byId.has(t.parentExternalId) || t.parentExternalId === t.externalId) {
      warnings.push(
        `row ${t.row}: parent ${t.parentExternalId} is not in the file; imported as a task`,
      );
      t.parentExternalId = null;
    }
  }
  const ordered: ClickUpCsvTask[] = [];
  const placed = new Set<string>();
  const place = (t: ClickUpCsvTask, depth: number) => {
    if (placed.has(t.externalId)) return;
    if (t.parentExternalId && !placed.has(t.parentExternalId) && depth < 16) {
      const parent = byId.get(t.parentExternalId);
      if (parent) place(parent, depth + 1);
    }
    if (t.parentExternalId && !placed.has(t.parentExternalId)) t.parentExternalId = null; // a cycle
    placed.add(t.externalId);
    ordered.push(t);
  };
  for (const t of tasks) place(t, 0);
  counts.tasks = ordered.filter((t) => t.parentExternalId === null).length;
  counts.subtasks = ordered.length - counts.tasks;

  return {
    spaces: [...spaces.values()],
    lists: [...lists.values()],
    people: [...people.values()].sort((a, b) => b.tasks - a.tasks || a.name.localeCompare(b.name)),
    tasks: ordered,
    counts,
    warnings,
  };
}
